import React, { useEffect, useState, createRef } from 'react';
import RichTextHyperLink from 'components/Core/HyperLink/RichTextHyperLink';
import Text from 'components/Core/Text';
import PropTypes from 'prop-types';
import { Container, Header, Links, TOCContainer } from './styles';

const RichTextTableOfContents = ({ data, maxHeightBoundary, classNames }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollTop, setScrollTop] = useState(true);
  const [scrollBottom, setScrollBottom] = useState(false);
  const linkRefs = data?.map(() => createRef(null));
  const scrollContainer = createRef(null);
  const numberOfLinks = data?.length;

  const min = 2;
  const max = 7;

  useEffect(() => {
    scrollContainer.current.onwheel = (e) => {
      e.preventDefault();
      window.scrollBy(0, e.deltaY);
    };
  }, [scrollContainer]);

  useEffect(() => {
    const top =
      activeIndex < min
        ? 0
        : linkRefs[activeIndex]?.current?.offsetTop - scrollContainer?.current?.offsetTop;

    if (scrollContainer?.current?.scrollTo instanceof Function) {
      scrollContainer?.current?.scrollTo({ top, behavior: 'smooth' });
    }
  }, [activeIndex]);

  const handleContainerScroll = (e) => {
    setScrollBottom(e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight);
    setScrollTop(e.target.scrollTop < 5);
  };

  const handleScroll = (e) => {
    const topOffset = 154 + 30 + 30; // max nav height + scroll offset + offset
    const offsetTopArray = data?.map((headerData) => {
      const anchor = headerData.anchor.split('#')[1];
      const heading = document.getElementById(anchor);
      if (heading?.nodeType == 1) {
        const rect = heading.getBoundingClientRect();
        return rect.top;
      }
      return null;
    });

    offsetTopArray.forEach((offset, index) => {
      if (offset < topOffset && (offsetTopArray[index + 1] || topOffset + 100) > topOffset) {
        setActiveIndex(index);
      }
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    const container = document.getElementById('table-of-contents');
    container.addEventListener('scroll', handleContainerScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scroll', handleContainerScroll);
    };
  });

  return (
    <TOCContainer className={classNames}>
      <Container
        scrollTop={scrollTop}
        scrollBottom={scrollBottom}
        numberOfLinks={numberOfLinks}
        className={classNames}
        min={min}
        max={max}
      >
        <Header>
          <Text className="toc--heading" size="20px" type="h6">
            Table of Contents
          </Text>
        </Header>
        {data && (
          <Links
            id="table-of-contents"
            maxHeightBoundary={maxHeightBoundary}
            activeIndex={activeIndex}
            ref={scrollContainer}
          >
            {data.map((ele, index) => (
              <RichTextHyperLink href={ele.anchor} key={index}>
                <div
                  className={`toc--link-container hover-borderless ${
                    activeIndex === index ? 'toc--link-active' : ''
                  }`}
                  ref={linkRefs[index]}
                >
                  <SectionTitle index={index} activeIndex={activeIndex} title={ele.title} />
                </div>
              </RichTextHyperLink>
            ))}
          </Links>
        )}
      </Container>
    </TOCContainer>
  );
};

RichTextTableOfContents.defaultProps = {
  data: [],
  min: 4,
  max: 7,
};

RichTextTableOfContents.propTypes = {
  data: PropTypes.array,
  min: PropTypes.number,
  max: PropTypes.number,
};

const SectionTitle = ({ index, activeIndex, title }) => {
  return (
    <Text
      size="16px"
      type="p"
      className={`toc--link${activeIndex === index ? ' active' : ''}`}
      key={index}
    >
      <span
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
        }}
      >
        {title}
      </span>
    </Text>
  );
};

export default RichTextTableOfContents;
